/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { css, cx } from "@panda/css";
import type { HTMLStyledProps } from "@panda/jsx";
import { styled } from "@panda/jsx";
import { forwardRef } from "react";

import { useScrollToAnchorId } from "@components/hooks/scroll.hooks";
import {
	sectionPaddingBlockEndCva,
	sectionPaddingBlockStartCva,
	sectionPaddingInlineCss,
	usePaddingBlockVariant,
} from "@components/utils/section.utils";

const sectionContainerStyles = css({
	position: "relative",
	marginInline: "auto",
});

export interface SectionContainerProps extends HTMLStyledProps<"section"> {
	anchorId?: string | null;
	animate?: boolean;
	disablePaddingBlock?: boolean;
	disablePaddingBlockStart?: boolean;
	disablePaddingBlockEnd?: boolean;
	disablePaddingInline?: boolean;
}

export const SectionContainer = forwardRef<HTMLElement, SectionContainerProps>(
	(
		{
			anchorId,
			children,
			className = "",
			disablePaddingBlock = false,
			disablePaddingInline = false,
			disablePaddingBlockStart = false,
			disablePaddingBlockEnd = false,
			...rest
		},
		forwardedRef,
	): React.JSX.Element => {
		const paddingBlockVariant = usePaddingBlockVariant();
		const isBlockStart = !disablePaddingBlock && !disablePaddingBlockStart;
		const isBlockEnd = !disablePaddingBlock && !disablePaddingBlockEnd;

		useScrollToAnchorId(anchorId);

		return (
			<styled.section
				id={anchorId ?? undefined}
				className={cx(
					"section-container",
					sectionContainerStyles,
					isBlockStart && sectionPaddingBlockStartCva({ paddingBlockVariant }),
					isBlockStart && "padding-block-start",
					isBlockEnd && sectionPaddingBlockEndCva({ paddingBlockVariant }),
					isBlockEnd && "padding-block-end",
					!disablePaddingInline && sectionPaddingInlineCss,
					!disablePaddingInline && "padding-inline",
					className,
				)}
				{...rest}
				ref={forwardedRef}
			>
				{children}
			</styled.section>
		);
	},
);

SectionContainer.displayName = "SectionContainer";
